import clsx from "clsx";
import { useEffect, useState, useRef } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import uniqid from "uniqid";

import i18n from "../../lib/i18n";
import theme from "@styles/theme";

const WithSelect = (props) => {
    const {
        isMulti,
        defaultValue,
        options,
        placeholder,
        disabled,
        hidden,
        size,
        isError,
        isTextWrap,
        hideSelectedOptions,
        closeMenuOnSelect,
        onChange,
        onFocus,
        onBlur,
        noOptionsMessage,
        limit,
        ...otherProps
    } = props;

    const [id] = useState(uniqid());
    const [Components] = useState(makeAnimated());
    const selectRef = useRef(null);

    const [defaultValue_, setDefaultValue_] = useState("");
    const [value, setValue] = useState("");

    useEffect(() => {
        if (isMulti) {
            setDefaultValue_(defaultValue);
            setValue(defaultValue);
        } else if (options) {
            setDefaultValue_(options.filter((v) => v.value === defaultValue));
            setValue(options.filter((v) => v.value === defaultValue));
        }
    }, [isMulti, defaultValue, options]);

    // input 요소에 직접 인라인 스타일 적용
    useEffect(() => {
        const applyInputStyle = () => {
            if (selectRef.current) {
                const inputElements = selectRef.current.querySelectorAll("input");
                inputElements.forEach((input) => {
                    input.style.setProperty("color", theme.palette.greySecondary.main, "important");
                });

                const selectedValueElements = selectRef.current.querySelectorAll(".select2-selection__single-value");

                selectedValueElements.forEach((input) => {
                    input.style.setProperty("color", theme.palette.greySecondary.main, "important");
                });
            }
        };

        const timer = setTimeout(applyInputStyle, 100);

        return () => clearTimeout(timer);
    }, []);

    return (
        <div ref={selectRef}>
            <Select
                id={id}
                className={clsx("select2-container", {
                    hidden,
                    "select2-container-sm": size === "sm",
                    "select2-container-text-wrap": isTextWrap,
                    "is-error": isError,
                })}
                classNamePrefix={"select2-selection"}
                components={Components}
                isDisabled={disabled}
                defaultValue={defaultValue_}
                value={value}
                onChange={onChange}
                onFocus={(e) => {
                    onFocus && onFocus(e);
                    e.target.closest(".select2-container").classList.add("select2-focus");
                }}
                onBlur={(e) => {
                    if (onBlur) onBlur(e);
                    e.target.closest(".select2-container").classList.remove("select2-focus");
                }}
                placeholder={placeholder === undefined ? i18n.t(`common::label::Select...`) : placeholder}
                options={options}
                isOptionDisabled={(option) => option.disabled}
                isMulti={isMulti}
                hideSelectedOptions={hideSelectedOptions !== undefined ? hideSelectedOptions : false}
                closeMenuOnSelect={closeMenuOnSelect !== undefined ? closeMenuOnSelect : true}
                noOptionsMessage={() => {
                    return noOptionsMessage ? noOptionsMessage : i18n.t(`common::label::No options`);
                }}
                menuPlacement={"auto"}
                {...otherProps}
                styles={{
                    control: (provided) => ({
                        ...provided,
                        minWidth: 120,
                    }),
                    menu: (provided) => ({
                        ...provided,
                        backgroundColor: theme.palette.background.default,
                    }),
                    menuList: (provided) => ({
                        ...provided,
                        backgroundColor: theme.palette.background.default,
                    }),
                    option: (provided, state) => ({
                        ...provided,
                        color: state.isSelected
                            ? `${theme.palette.primary.main} !important`
                            : theme.palette.greySecondary.main,
                        backgroundColor: state.isSelected ? theme.palette.primary.light : provided.backgroundColor,
                        "&:active": {
                            color: state.isSelected
                                ? `${theme.palette.primary.main} !important`
                                : theme.palette.greySecondary.main,
                        },
                        "&.select2-selection__option--is-selected": {
                            color: `${theme.palette.primary.main} !important`,
                        },
                        "&:hover": {
                            backgroundColor: theme.palette.greySecondary[50],
                            color: state.isSelected
                                ? `${theme.palette.primary.main} !important`
                                : theme.palette.greySecondary.main,
                        },
                        "&.select2-selection__option--is-focused": {
                            backgroundColor: theme.palette.greySecondary[50],
                            color: state.isSelected
                                ? `${theme.palette.primary.main} !important`
                                : theme.palette.greySecondary.main,
                        },
                    }),
                }}
            />
        </div>
    );
};

const withCheckbox = (props) => {
    let { data, innerProps, getValue } = props;
    let selectedOptions = getValue();
    let _id = uniqid();

    return (
        <div {...innerProps}>
            <div className={"form-check mt-1 mb-1 ml-2 "}>
                <label htmlFor={_id} className={"form-check-label"}>
                    <input
                        id={_id}
                        type={"checkbox"}
                        className={"form-check-input"}
                        checked={selectedOptions.filter((v) => v.value === data.value).length > 0}
                        value={data.value}
                        readOnly={true}
                    />
                    <i className={"input-helper"}></i>
                    <span>{data.label}</span>
                </label>
            </div>
        </div>
    );
};

WithSelect.defaultProps = {
    isMulti: false,
};

export default WithSelect;
